import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';

import { useAiDataPolicyRole } from '@shared/ai-data-policy/access';
import { useAiDataPolicyStore } from '@shared/ai-data-policy/store';

import { IN_HOUSE_GROUP, type ModelOption } from '../data/models';
import type { RouteModel } from '../types';

import { DATA_POLICY_ENABLED } from './flag';
import { goToDataPolicySettings } from './navigation';
import { usePolicyPromptStore } from './prompt.store';

export interface PickerPolicy {
  rule: 'internal' | 'commercial';
  decided: boolean;
  setByName: string | null;
  canChange: boolean;
  chatHasInvestorData: boolean;
  onChoose: (shown: ModelOption | null) => void;
  onOpenSettings: () => void;
  onStartNewChat?: (modelId: string) => void;
  refresh: () => void;
}

export function routeModelOf(option: ModelOption): RouteModel {
  return {
    id: option.id,
    label: option.label,
    group: option.group,
    providerLabel: option.group.label,
    kind: option.group.id === IN_HOUSE_GROUP ? 'internal' : 'commercial',
  };
}

export function usePickerPolicy(options: {
  chatHasInvestorData: boolean;
  onStartNewChat?: (modelId: string) => void;
}): PickerPolicy | undefined {
  const { chatHasInvestorData, onStartNewChat } = options;
  const policy = useAiDataPolicyStore((s) => s.policy);
  const role = useAiDataPolicyRole();
  const navigate = useNavigate();

  const onChoose = useCallback((shown: ModelOption | null) => {
    void usePolicyPromptStore.getState().open({
      variant: 'manual',
      investorName: null,
      categories: [],
      selectedModel: shown ? routeModelOf(shown) : null,
    });
  }, []);
  const onOpenSettings = useCallback(() => {
    goToDataPolicySettings(navigate);
  }, [navigate]);
  const refresh = useCallback(() => {
    void useAiDataPolicyStore.getState().load();
  }, []);

  return useMemo(() => {
    if (!DATA_POLICY_ENABLED) return undefined;
    const value = policy?.value ?? 'not_chosen';
    return {
      rule: value === 'commercial_allowed' ? 'commercial' : 'internal',
      decided: value !== 'not_chosen',
      setByName: policy?.setBy?.name ?? null,
      canChange: role === 'admin',
      chatHasInvestorData,
      onChoose,
      onOpenSettings,
      onStartNewChat,
      refresh,
    };
  }, [policy, role, chatHasInvestorData, onChoose, onOpenSettings, onStartNewChat, refresh]);
}
